import React, { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import styles from "./PlatformTerms.module.scss";
import {
  creatorCooperationSections,
  newsletterRegulationsSections,
  privacyPolicySections,
  regulaminSections,
} from "../Constants/Platform.constant";

const sections = [
  { id: "regulamin", labelId: "legal.platform.side.regulamin", defaultLabel: "Platform terms of service" },
  { id: "privacy", labelId: "legal.platform.side.privacy", defaultLabel: "Privacy policy, GDPR and personal data protection" },
  { id: "designers", labelId: "legal.platform.side.designers", defaultLabel: "Creator cooperation terms" },
  { id: "newsLetter", labelId: "legal.platform.side.newsletter", defaultLabel: "Newsletter terms" },
];

const getSectionId = (page: string | null) => {
  if (!page) return null;
  const mapping: Record<string, string> = {
    "privacy-policy": "privacy",
    "terms-of-service": "regulamin",
    "designers": "designers",
    "newsLetter": "newsLetter",
  };
  return mapping[page] || null;
};

type LegalBlock = {
  id: string;
  title: string;
  content?: string[];
  items?: string[];
  checkbox?: string;
  afterCheckbox?: string;
};

const sectionContent: Record<string, LegalBlock[]> = {
  regulamin: regulaminSections,
  privacy: privacyPolicySections,
  designers: creatorCooperationSections,
  newsLetter: newsletterRegulationsSections,
};

const renderLegalBlocks = (blocks: LegalBlock[]) =>
  blocks.map((sec, idx) => (
    <div key={sec.id} className={styles.section}>
      <p className={styles.sectionTitle}>{sec.title.split(" ")[0]}</p>
      <p className={styles.sectionSubtitle}>{sec.title.replace(/^§\d+\s+/, "")}</p>

      {sec.content?.map((para, i) => (
        <p key={i} className={styles.paragraph}>
          {i + 1}.{"  "}
          {para}
        </p>
      ))}

      {sec.items && (
        <ol className={styles.list}>
          {sec.items.map((item, i) => (
            <li key={i} className={styles.listItem}>
              {i + 1}.{"  "}
              {item}
            </li>
          ))}
        </ol>
      )}

      {sec.checkbox && <span className={styles.checkboxText}>{sec.checkbox}</span>}
      {sec.afterCheckbox && <p className={styles.paragraph}>{sec.afterCheckbox}</p>}

      {idx < blocks.length - 1 && <hr className={styles.divider} />}
    </div>
  ));

const PlatformTerms: React.FC = () => {
  const [activeSection, setActiveSection] = useState("regulamin");
  const queryParams = new URLSearchParams(window.location.search);
  const page = queryParams.get("page");
  const sectionId = getSectionId(page);

  
  useEffect(() => {
    if (sectionId && sections.some((s) => s.id === sectionId)) {
      document.body.scrollTo({ top: 0, behavior: "instant" });
      setActiveSection(sectionId);
      //  cleanup the url after setting the section to avoid confusion when sharing links
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [sectionId]);

  const renderDocument = (sectionId: string) => {
    const meta = sections.find((s) => s.id === sectionId);
    const blocks = sectionContent[sectionId];

    if (!meta || !blocks) return null;

    return (
      <>
        <h2 className={styles.docTitle}>
          <FormattedMessage id={meta.labelId} defaultMessage={meta.defaultLabel} />
        </h2>
        {renderLegalBlocks(blocks)}
      </>
    );
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.pageTitle}>
          <FormattedMessage id="legal.platform.title" defaultMessage="Service terms" />
        </h1>
        <p className={styles.pageSubtitle}>
          <FormattedMessage
            id="legal.platform.subtitle"
            defaultMessage="Before you register, please read the service terms."
          />
        </p>
      </div>

      <div className={styles.mainContainer}>
        {/* Desktop: sidebar + content */}
        <div className={styles.desktopLayout}>
          <aside className={styles.sidebar}>
            {sections.map((sec) => (
              <button
                key={sec.id}
                type="button"
                className={`${styles.sidebarItem} ${activeSection === sec.id ? styles.active : ""}`}
                onClick={() => setActiveSection(sec.id)}
              >
                <FormattedMessage id={sec.labelId} defaultMessage={sec.defaultLabel} />
              </button>
            ))}
          </aside>

          <main className={styles.main}>{renderDocument(activeSection)}</main>
        </div>

        {/* Mobile: accordion */}
        <div className={styles.mobileLayout}>
          {sections.map((sec) => {
            const isOpen = activeSection === sec.id;
            return (
              <div key={sec.id} className={styles.accordionItem}>
                <button
                  type="button"
                  className={`${styles.accordionHeader} ${isOpen ? styles.accordionHeaderOpen : ""}`}
                  onClick={() => setActiveSection(sec.id)}
                  aria-expanded={isOpen}
                >
                  <span className={styles.accordionLabel}>
                    <FormattedMessage id={sec.labelId} defaultMessage={sec.defaultLabel} />
                  </span>
                  <span className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ""}`} aria-hidden />
                </button>
                {isOpen && (
                  <div className={styles.accordionContent}>{renderDocument(sec.id)}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PlatformTerms;